import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Play, Pause, Eye, MoreVertical, Search, 
  DollarSign, Target, Calendar, ChevronRight, Film, Percent, Edit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayerCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    creatives: [], // [{creative_id, weight}]
    budget_impressions: '',
    budget_amount: '',
    cpm_rate: 5.0,
    start_date: '',
    end_date: '',
    target_screens: [],
    status: 'draft'
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data);
      }
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCreatives = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creatives?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreatives(data);
      }
    } catch (error) {
      console.error('Failed to fetch creatives');
    }
  }, []);

  const fetchScreens = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScreens(data);
      }
    } catch (error) {
      console.error('Failed to fetch screens');
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchCreatives();
    fetchScreens();
  }, [fetchCampaigns, fetchCreatives, fetchScreens]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.creatives.length === 0) {
      toast.error('Please select at least one creative');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        budget_impressions: formData.budget_impressions ? parseInt(formData.budget_impressions) : null,
        budget_amount: formData.budget_amount ? parseFloat(formData.budget_amount) : null,
        cpm_rate: parseFloat(formData.cpm_rate)
      };
      
      const isEditing = !!editingCampaign;
      const url = isEditing 
        ? `${API_URL}/api/player-campaigns/${editingCampaign.id}`
        : `${API_URL}/api/player-campaigns`;
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(isEditing ? 'Campaign updated successfully' : 'Campaign created successfully');
        setShowDialog(false);
        setEditingCampaign(null);
        resetForm();
        fetchCampaigns();
      } else {
        const text = await res.text();
        let errorMsg = isEditing ? 'Failed to update campaign' : 'Failed to create campaign';
        try {
          const err = JSON.parse(text);
          errorMsg = err.detail || errorMsg;
        } catch {
          if (text) errorMsg = text;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(editingCampaign ? 'Failed to update campaign' : 'Failed to create campaign');
    }
  };

  const handleEdit = (campaign) => {
    // Convert campaign data to form format
    setFormData({
      name: campaign.name || '',
      description: campaign.description || '',
      creatives: campaign.creatives?.map(c => ({
        creative_id: c.creative_id,
        weight: c.weight || 100
      })) || [],
      budget_impressions: campaign.budget_impressions?.toString() || '',
      budget_amount: campaign.budget_amount?.toString() || '',
      cpm_rate: campaign.cpm_rate || 5.0,
      start_date: campaign.start_date ? campaign.start_date.slice(0, 16) : '',
      end_date: campaign.end_date ? campaign.end_date.slice(0, 16) : '',
      target_screens: campaign.target_screens || [],
      status: campaign.status || 'draft'
    });
    setEditingCampaign(campaign);
    setShowDialog(true);
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player-campaigns/${campaignId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Campaign ${newStatus}`);
        fetchCampaigns();
      } else {
        toast.error('Failed to update campaign');
      }
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player-campaigns/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Campaign deleted');
        fetchCampaigns();
      } else {
        toast.error('Failed to delete campaign');
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const toggleCreative = (creativeId) => {
    const existing = formData.creatives.find(c => c.creative_id === creativeId);
    if (existing) {
      setFormData({
        ...formData,
        creatives: formData.creatives.filter(c => c.creative_id !== creativeId)
      });
    } else {
      setFormData({
        ...formData,
        creatives: [...formData.creatives, { creative_id: creativeId, weight: 100 }]
      });
    }
  };

  const updateCreativeWeight = (creativeId, weight) => {
    setFormData({
      ...formData,
      creatives: formData.creatives.map(c => 
        c.creative_id === creativeId ? { ...c, weight: parseInt(weight) || 100 } : c
      )
    });
  };

  const toggleScreen = (screenId) => {
    if (formData.target_screens.includes(screenId)) {
      setFormData({
        ...formData,
        target_screens: formData.target_screens.filter(id => id !== screenId)
      });
    } else {
      setFormData({
        ...formData,
        target_screens: [...formData.target_screens, screenId]
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      creatives: [],
      budget_impressions: '',
      budget_amount: '',
      cpm_rate: 5.0,
      start_date: '',
      end_date: '',
      target_screens: [],
      status: 'draft'
    });
    setEditingCampaign(null);
  };

  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400';
      case 'paused': return 'bg-amber-500/20 text-amber-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getBudgetProgress = (campaign) => {
    if (campaign.budget_impressions) {
      return (campaign.spent_impressions / campaign.budget_impressions) * 100;
    }
    if (campaign.budget_amount) {
      return (campaign.spent_amount / campaign.budget_amount) * 100;
    }
    return 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Player Campaigns</h1>
          <p className="text-zinc-400 mt-1">Create and manage ad campaigns for your players</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Campaigns</p>
                <p className="text-2xl font-bold text-white">{campaigns.length}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Target className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Active</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {campaigns.filter(c => c.status === 'active').length}
                </p>
              </div>
              <div className="p-3 bg-emerald-500/20 rounded-lg">
                <Play className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Impressions</p>
                <p className="text-2xl font-bold text-white">
                  {formatNumber(campaigns.reduce((sum, c) => sum + (c.spent_impressions || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Eye className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Spend</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(campaigns.reduce((sum, c) => sum + (c.spent_amount || 0), 0))}
                </p>
              </div>
              <div className="p-3 bg-amber-500/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-700"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-700">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card className="glass-card border-zinc-800">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500">Campaign</TableHead>
              <TableHead className="text-zinc-500">Creatives</TableHead>
              <TableHead className="text-zinc-500">Budget</TableHead>
              <TableHead className="text-zinc-500">Progress</TableHead>
              <TableHead className="text-zinc-500">CPM</TableHead>
              <TableHead className="text-zinc-500">Dates</TableHead>
              <TableHead className="text-zinc-500">Status</TableHead>
              <TableHead className="text-zinc-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                  Loading campaigns...
                </TableCell>
              </TableRow>
            ) : filteredCampaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                  No campaigns found. Create your first campaign!
                </TableCell>
              </TableRow>
            ) : (
              filteredCampaigns.map((campaign) => (
                <TableRow key={campaign.id} className="border-zinc-800" data-testid={`campaign-row-${campaign.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-white">{campaign.name}</p>
                      {campaign.description && (
                        <p className="text-zinc-500 text-sm truncate max-w-[200px]">{campaign.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Film className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-300">{campaign.creatives?.length || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {campaign.budget_impressions && (
                        <p className="text-zinc-300">{formatNumber(campaign.budget_impressions)} imps</p>
                      )}
                      {campaign.budget_amount && (
                        <p className="text-zinc-300">{formatCurrency(campaign.budget_amount)}</p>
                      )}
                      {!campaign.budget_impressions && !campaign.budget_amount && (
                        <span className="text-zinc-500">Unlimited</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="w-32">
                      <Progress value={getBudgetProgress(campaign)} className="h-2" />
                      <p className="text-xs text-zinc-500 mt-1">
                        {formatNumber(campaign.spent_impressions || 0)} imps / {formatCurrency(campaign.spent_amount || 0)}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-300">{formatCurrency(campaign.cpm_rate)}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p className="text-zinc-400">{new Date(campaign.start_date).toLocaleDateString()}</p>
                      <p className="text-zinc-500">to {new Date(campaign.end_date).toLocaleDateString()}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(campaign.status)}>{campaign.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                        <DropdownMenuItem onClick={() => { setSelectedCampaign(campaign); setShowDetails(true); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(campaign)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Campaign
                        </DropdownMenuItem>
                        {campaign.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(campaign.id, 'active')}>
                            <Play className="w-4 h-4 mr-2" />
                            Activate
                          </DropdownMenuItem>
                        )}
                        {campaign.status === 'active' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(campaign.id, 'paused')}>
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </DropdownMenuItem>
                        )}
                        {campaign.status === 'paused' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(campaign.id, 'active')}>
                            <Play className="w-4 h-4 mr-2" />
                            Resume
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDelete(campaign.id)} className="text-rose-400 focus:text-rose-400">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Campaign Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingCampaign ? 'Edit Player Campaign' : 'Create Player Campaign'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Basic Info</h3>
              <div className="space-y-2">
                <Label className="text-zinc-300">Campaign Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Summer Sale Campaign"
                  className="bg-zinc-800 border-zinc-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Description (optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Campaign description..."
                  className="bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>
            </div>

            {/* Creatives Selection */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                Select Creatives ({formData.creatives.length} selected)
              </h3>
              <div className="max-h-48 overflow-y-auto border border-zinc-800 rounded-lg">
                {creatives.length === 0 ? (
                  <p className="p-4 text-zinc-500 text-center">No active creatives. Create some first!</p>
                ) : (
                  creatives.map((creative) => {
                    const isSelected = formData.creatives.some(c => c.creative_id === creative.id);
                    const selectedCreative = formData.creatives.find(c => c.creative_id === creative.id);
                    return (
                      <div 
                        key={creative.id} 
                        className={`flex items-center justify-between p-3 border-b border-zinc-800 last:border-0 ${isSelected ? 'bg-blue-500/10' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleCreative(creative.id)}
                          />
                          <div className="w-12 h-8 bg-zinc-800 rounded overflow-hidden">
                            {creative.creative_type === 'image' ? (
                              <img src={creative.file_url.startsWith('http') ? creative.file_url : `${API_URL}${creative.file_url}`} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film className="w-4 h-4 text-zinc-500" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-white text-sm">{creative.name}</p>
                            <p className="text-zinc-500 text-xs">{creative.creative_type} • {creative.duration_seconds}s</p>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <Label className="text-zinc-500 text-xs">Weight</Label>
                            <Input
                              type="number"
                              value={selectedCreative?.weight || 100}
                              onChange={(e) => updateCreativeWeight(creative.id, e.target.value)}
                              className="w-20 h-8 bg-zinc-800 border-zinc-700 text-sm"
                              min="1"
                              max="1000"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <p className="text-zinc-500 text-xs">Higher weight = more frequent rotation. Example: weight 200 plays 2x more often than weight 100.</p>
            </div>

            {/* Budget */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Budget</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Impressions Limit</Label>
                  <Input
                    type="number"
                    value={formData.budget_impressions}
                    onChange={(e) => setFormData({ ...formData, budget_impressions: e.target.value })}
                    placeholder="10000"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Dollar Budget</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.budget_amount}
                    onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
                    placeholder="500.00"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">CPM Rate ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cpm_rate}
                    onChange={(e) => setFormData({ ...formData, cpm_rate: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
              </div>
              <p className="text-zinc-500 text-xs">Leave budget fields empty for unlimited. Campaign stops when either limit is reached.</p>
            </div>

            {/* Dates */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Schedule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Start Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">End Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Target Screens (Optional) */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                Target Screens (Optional) - {formData.target_screens.length} selected
              </h3>
              <div className="max-h-32 overflow-y-auto border border-zinc-800 rounded-lg">
                {screens.map((screen) => (
                  <div key={screen.id} className="flex items-center gap-3 p-2 border-b border-zinc-800 last:border-0">
                    <Checkbox
                      checked={formData.target_screens.includes(screen.id)}
                      onCheckedChange={() => toggleScreen(screen.id)}
                    />
                    <span className="text-white text-sm">{screen.name}</span>
                    <span className="text-zinc-500 text-xs">{screen.location}</span>
                  </div>
                ))}
              </div>
              <p className="text-zinc-500 text-xs">Leave empty to target all screens.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedCampaign?.name}
              <Badge className={getStatusColor(selectedCampaign?.status)}>{selectedCampaign?.status}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedCampaign && (
            <div className="space-y-6">
              {/* Description */}
              {selectedCampaign.description && (
                <p className="text-zinc-400">{selectedCampaign.description}</p>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-800 rounded-lg p-4">
                  <p className="text-zinc-400 text-sm">Impressions</p>
                  <p className="text-2xl font-bold text-white">{formatNumber(selectedCampaign.spent_impressions)}</p>
                  {selectedCampaign.budget_impressions && (
                    <p className="text-zinc-500 text-sm">of {formatNumber(selectedCampaign.budget_impressions)}</p>
                  )}
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <p className="text-zinc-400 text-sm">Spend</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(selectedCampaign.spent_amount)}</p>
                  {selectedCampaign.budget_amount && (
                    <p className="text-zinc-500 text-sm">of {formatCurrency(selectedCampaign.budget_amount)}</p>
                  )}
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <p className="text-zinc-400 text-sm">CPM</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(selectedCampaign.cpm_rate)}</p>
                </div>
              </div>

              {/* Active Creatives Details */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  Active Creatives ({selectedCampaign.creatives?.length || 0})
                </h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                  {selectedCampaign.creatives?.length === 0 ? (
                    <p className="text-zinc-500 text-center py-4">No creatives assigned</p>
                  ) : (
                    selectedCampaign.creatives?.map((c, idx) => {
                      const creative = c.creative || creatives.find(cr => cr.id === c.creative_id);
                      const fileUrl = creative?.file_url?.startsWith('http') 
                        ? creative.file_url 
                        : `${API_URL}${creative?.file_url || ''}`;
                      
                      return (
                        <div key={idx} className="bg-zinc-800 rounded-lg p-4 space-y-3">
                          {/* Creative Header */}
                          <div className="flex items-start gap-4">
                            <div className="w-24 h-16 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                              {creative?.creative_type === 'image' ? (
                                <img src={fileUrl} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <video src={fileUrl} className="w-full h-full object-cover" muted />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-white font-medium truncate">{creative?.name || 'Unknown Creative'}</p>
                                <Badge className={creative?.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}>
                                  {creative?.status || 'unknown'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-1">
                                <span className="text-zinc-400 text-sm capitalize">{creative?.creative_type || 'unknown'}</span>
                                <span className="text-zinc-500 text-sm">{creative?.duration_seconds || 0}s</span>
                                <span className="text-zinc-500 text-sm">{creative?.width || 0}x{creative?.height || 0}</span>
                              </div>
                            </div>
                          </div>

                          {/* Creative Details Grid */}
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-700">
                            <div>
                              <p className="text-zinc-500 text-xs uppercase">Weight</p>
                              <p className="text-zinc-300 text-sm">{c.weight}%</p>
                            </div>
                            <div>
                              <p className="text-zinc-500 text-xs uppercase">MIME Type</p>
                              <p className="text-zinc-300 text-sm">{creative?.mime_type || 'N/A'}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-zinc-500 text-xs uppercase">Click URL</p>
                              <p className="text-cyan-400 text-sm truncate">
                                {creative?.click_url || <span className="text-zinc-500">Not set</span>}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-zinc-500 text-xs uppercase">File URL</p>
                              <p className="text-blue-400 text-sm truncate">{fileUrl}</p>
                            </div>
                          </div>

                          {/* Creative Stats */}
                          <div className="flex items-center gap-4 pt-2 border-t border-zinc-700">
                            <div className="flex items-center gap-1">
                              <Eye className="w-3 h-3 text-zinc-500" />
                              <span className="text-zinc-400 text-xs">{formatNumber(creative?.impressions || 0)} impressions</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Target className="w-3 h-3 text-zinc-500" />
                              <span className="text-zinc-400 text-xs">{formatNumber(creative?.clicks || 0)} clicks</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Schedule */}
              <div>
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">Schedule</h3>
                <div className="grid grid-cols-2 gap-4 bg-zinc-800 rounded-lg p-4">
                  <div>
                    <p className="text-zinc-500 text-xs uppercase">Start Date</p>
                    <p className="text-white">{new Date(selectedCampaign.start_date).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase">End Date</p>
                    <p className="text-white">{new Date(selectedCampaign.end_date).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Target Screens */}
              {selectedCampaign.target_screens?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">
                    Target Screens ({selectedCampaign.target_screens.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCampaign.target_screens.map((screenId, idx) => {
                      const screen = screens.find(s => s.id === screenId);
                      return (
                        <Badge key={idx} className="bg-zinc-800 text-zinc-300">
                          {screen?.name || screenId.slice(0, 8)}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Campaign ID */}
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-zinc-500 text-xs">Campaign ID: <code className="text-zinc-400">{selectedCampaign.id}</code></p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
